import type { IceServer } from "../realtime/assistClient";

/** Messaggi del canale dati "annotations" (PROGETTAZIONE §7). */
export type AnnotationMessage =
  | { t: "arrow"; id: string; x: number; y: number; ts: number; seq: number; color?: string }
  | { t: "clear" }
  | { t: "remove"; id: string }
  | { t: "ping"; ts: number }
  | { t: "pong"; ts: number; rx: number }
  | { t: "diag"; camera: string; localFrames: number; remoteFrames: number; codec: string; platform: string }
  | { t: "arrowpos"; items: { id: string; x: number; y: number; v: boolean }[] };

export interface PeerCallbacks {
  onIceCandidate(candidate: string, sdpMid: string | null, sdpMLineIndex: number | null): void;
  onRemoteStream(stream: MediaStream): void;
  onDataMessage(msg: AnnotationMessage): void;
  onDataOpen(): void;
  onStateChange(state: RTCPeerConnectionState, ice: RTCIceConnectionState): void;
  onClockSync(rttMs: number, offsetMs: number): void;
}

/**
 * Incapsula RTCPeerConnection per una chiamata: tracce locali, flusso remoto, canale dati "annotations",
 * scambio SDP/ICE (trickle, con coda dei candidati arrivati prima della descrizione remota) e stima
 * del ritardo con ping/pong (serve per il tempo del frame delle frecce).
 */
export class CallPeer {
  readonly pc: RTCPeerConnection;
  private channel: RTCDataChannel | null = null;
  private readonly remoteStream = new MediaStream();
  private pendingIce: RTCIceCandidateInit[] = [];
  private remoteSet = false;
  private seq = 0;
  private pingTimer: number | null = null;
  rttMs = 0;
  /** differenza stimata (orologio remoto - orologio locale), in ms */
  clockOffsetMs = 0;

  constructor(iceServers: IceServer[], private readonly cb: PeerCallbacks) {
    this.pc = new RTCPeerConnection({ iceServers, iceCandidatePoolSize: 2 });
    this.pc.onicecandidate = (e) => {
      if (e.candidate) cb.onIceCandidate(e.candidate.candidate, e.candidate.sdpMid, e.candidate.sdpMLineIndex);
    };
    // MixedReality-WebRTC (HoloLens) non associa le tracce a uno stream (nessun msid): raccolgo le tracce
    // in uno stream locale e lo segnalo una volta sola.
    this.pc.ontrack = (e) => {
      if (e.streams[0]) { cb.onRemoteStream(e.streams[0]); return; }
      this.remoteStream.addTrack(e.track);
      cb.onRemoteStream(this.remoteStream); // a ogni traccia (audio, poi video): l'elemento <video> si riaggancia
    };
    this.pc.ondatachannel = (e) => this.attachChannel(e.channel);
    const report = () => cb.onStateChange(this.pc.connectionState, this.pc.iceConnectionState);
    this.pc.onconnectionstatechange = report;
    this.pc.oniceconnectionstatechange = report;
  }

  /**
   * H264 per primo nell'offerta (28/10/2026): sul visore l'H264 usa il codificatore hardware, il VP8 è software e lo rallenta.
   * Quando chiama il visore è lui a imporlo nella sua offerta; quando chiama il browser deve farlo l'offerta del browser
   * (il visore non può più filtrare l'offerta remota: il suo filtro la rendeva invalida). Se il browser non espone H264 non cambia nulla.
   */
  private preferH264(): void {
    if (typeof RTCRtpSender === "undefined" || !RTCRtpSender.getCapabilities) return;
    const caps = RTCRtpSender.getCapabilities("video");
    if (!caps) return;
    const h264 = caps.codecs.filter((c) => c.mimeType.toLowerCase() === "video/h264");
    if (h264.length === 0) return;
    const rest = caps.codecs.filter((c) => c.mimeType.toLowerCase() !== "video/h264");
    for (const t of this.pc.getTransceivers()) {
      if (t.receiver.track.kind !== "video" || !t.setCodecPreferences) continue;
      try { t.setCodecPreferences([...h264, ...rest]); } catch (e) { console.warn("setCodecPreferences", e); }
    }
  }

  addLocalStream(stream: MediaStream): void {
    for (const track of stream.getTracks()) this.pc.addTrack(track, stream);
  }

  /** Il chiamante crea il canale dati e l'offerta. */
  async createOffer(): Promise<string> {
    this.attachChannel(this.pc.createDataChannel("annotations", { ordered: true }));
    this.preferH264();
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    return offer.sdp ?? "";
  }

  async acceptOffer(sdp: string): Promise<string> {
    await this.pc.setRemoteDescription({ type: "offer", sdp });
    this.remoteSet = true;
    await this.flushIce();
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    return answer.sdp ?? "";
  }

  async acceptAnswer(sdp: string): Promise<void> {
    await this.pc.setRemoteDescription({ type: "answer", sdp });
    this.remoteSet = true;
    await this.flushIce();
  }

  async addRemoteIce(candidate: string, sdpMid: string | null, sdpMLineIndex: number | null): Promise<void> {
    const init: RTCIceCandidateInit = { candidate, sdpMid: sdpMid ?? undefined, sdpMLineIndex: sdpMLineIndex ?? undefined };
    if (!this.remoteSet) { this.pendingIce.push(init); return; }
    await this.pc.addIceCandidate(init);
  }

  private async flushIce(): Promise<void> {
    const list = this.pendingIce; this.pendingIce = [];
    for (const c of list) await this.pc.addIceCandidate(c);
  }

  private attachChannel(ch: RTCDataChannel): void {
    this.channel = ch;
    ch.onopen = () => {
      this.cb.onDataOpen();
      this.pingTimer = window.setInterval(() => this.send({ t: "ping", ts: Date.now() }), 2000);
    };
    ch.binaryType = "arraybuffer";
    ch.onmessage = (e) => {
      // il HoloLens manda i messaggi come byte UTF-8 (binario), il browser come testo
      const text = typeof e.data === "string" ? e.data : new TextDecoder().decode(e.data as ArrayBuffer);
      let msg: AnnotationMessage;
      try { msg = JSON.parse(text) as AnnotationMessage; } catch { console.warn("[MDT] messaggio dati non valido:", text); return; }
      if (msg.t === "ping") { this.send({ t: "pong", ts: msg.ts, rx: Date.now() }); return; }
      if (msg.t === "pong") {
        const now = Date.now();
        this.rttMs = now - msg.ts;
        // offset = orologio remoto - locale, stimato al momento della ricezione remota
        this.clockOffsetMs = msg.rx - (msg.ts + this.rttMs / 2);
        this.cb.onClockSync(this.rttMs, this.clockOffsetMs);
        return;
      }
      this.cb.onDataMessage(msg);
    };
  }

  send(msg: AnnotationMessage): boolean {
    if (!this.channel || this.channel.readyState !== "open") return false;
    this.channel.send(JSON.stringify(msg));
    return true;
  }

  /**
   * Invia una freccia: (x, y) normalizzati sul video, con il tempo del frame mostrato espresso
   * nell'orologio del dispositivo remoto (HoloLens), che è quello usato dal registratore delle pose.
   */
  sendArrow(x: number, y: number, frameTimeLocalMs: number, color?: string): AnnotationMessage {
    const msg: AnnotationMessage = {
      t: "arrow",
      id: crypto.randomUUID(),
      x, y,
      ts: Math.round(frameTimeLocalMs + this.clockOffsetMs),
      seq: ++this.seq,
      color,
    };
    this.send(msg);
    return msg;
  }

  close(): void {
    if (this.pingTimer) window.clearInterval(this.pingTimer);
    this.channel?.close();
    this.pc.getSenders().forEach((s) => s.track?.stop());
    this.pc.close();
  }
}

/** Statistiche essenziali del video ricevuto (risoluzione, fps, bitrate, RTT ICE) per le misure di M1. */
export async function readStats(pc: RTCPeerConnection, prev: { bytes: number; t: number }) {
  const stats = await pc.getStats();
  let out = { width: 0, height: 0, fps: 0, kbps: 0, rttMs: 0, candidate: "", codec: "", framesDecoded: 0, outCodec: "", outKB: 0, outFrames: 0 };
  const now = performance.now();
  stats.forEach((s) => {
    if (s.type === "outbound-rtp" && s.kind === "video") {
      out.outCodec = (stats.get(s.codecId)?.mimeType ?? "").replace("video/", "");
      out.outKB = Math.round((s.bytesSent ?? 0) / 1024); out.outFrames = s.framesEncoded ?? 0;
    }
    if (s.type === "inbound-rtp" && s.kind === "video") {
      out.width = s.frameWidth ?? 0; out.height = s.frameHeight ?? 0; out.fps = Math.round(s.framesPerSecond ?? 0);
      out.codec = (stats.get(s.codecId)?.mimeType ?? "").replace("video/", ""); out.framesDecoded = s.framesDecoded ?? 0;
      const bytes = s.bytesReceived ?? 0;
      if (prev.t) out.kbps = Math.round(((bytes - prev.bytes) * 8) / (now - prev.t));
      prev.bytes = bytes; prev.t = now;
    }
    if (s.type === "candidate-pair" && s.state === "succeeded" && s.nominated) {
      out.rttMs = Math.round((s.currentRoundTripTime ?? 0) * 1000);
      const local = stats.get(s.localCandidateId);
      out.candidate = local?.candidateType ?? "";
    }
  });
  return out;
}
